/**
 * IncomeVsCOGSChart Component
 * Used in: Dashboard screen - displays Income vs COGS Trend (Monthly)
 */
import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Label } from 'recharts';
import { FinancialData, getDataByViewMode } from '../data/financialData';
import { MobileChartFallback } from './MobileChartFallback';
import { useScreenSize } from '../hooks/useScreenSize';

interface IncomeVsCOGSChartProps {
  viewMode?: 'monthly' | 'yearly';
  data: FinancialData[];
}

export const IncomeVsCOGSChart: React.FC<IncomeVsCOGSChartProps> = ({ viewMode = 'monthly', data }) => {
  const { shouldShowFallback } = useScreenSize();
  
  // Get data based on view mode
  const chartData = useMemo(() => getDataByViewMode(viewMode, data), [viewMode, data]);
  
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate mean values for the series
  const meanValues = useMemo(() => {
    const totalIncomeSum = chartData.reduce((sum, item) => sum + item.totalIncome, 0);
    const totalCOGSSum = chartData.reduce((sum, item) => sum + item.totalCOGS, 0);
    
    return {
      totalIncome: totalIncomeSum / chartData.length,
      totalCOGS: totalCOGSSum / chartData.length,
    };
  }, [chartData]);

  // Custom tick formatter for quarterly display
  const formatXAxisTick = (tickItem: string) => {
    // Only show labels for quarter-end months (Mar, Jun, Sep, Dec)
    const monthName = tickItem.split(' ')[0];
    const quarterEndMonths = ['Mar', 'Jun', 'Sep', 'Dec'];
    
    if (quarterEndMonths.includes(monthName)) {
      return tickItem;
    }
    return ''; // Return empty string for non-quarter-end months (will still show tick mark)
  };

  // Custom tick component to ensure all months get tick marks
  const CustomTick = (props: any) => {
    const { x, y, payload } = props;
    const monthName = payload.value.split(' ')[0];
    const quarterEndMonths = ['Mar', 'Jun', 'Sep', 'Dec'];
    const showLabel = quarterEndMonths.includes(monthName);
    
    return (
      <g transform={`translate(${x},${y})`}>
        {/* Always render the tick mark */}
        <line x1={0} y1={0} x2={0} y2={6} stroke="#6b7280" />
        {/* Only render text for quarter-end months */}
        {showLabel && (
          <text 
            x={0} 
            y={12} 
            dy={8} 
            textAnchor="end" 
            fill="#6b7280" 
            fontSize="11"
            transform="rotate(-45)"
          >
            {payload.value}
          </text>
        )}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Show fallback for very narrow screens
  if (shouldShowFallback) {
    return (
      <MobileChartFallback
        title={`Income vs COGS Trend (${viewMode === 'yearly' ? 'Yearly' : 'Monthly'})`}
        data={chartData}
        dataKeys={['totalIncome', 'totalCOGS']}
        formatValue={formatCurrency}
        showRotateMessage={true}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Income vs COGS Trend ({viewMode === 'yearly' ? 'Yearly' : 'Monthly'})
      </h3>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <LineChart 
            data={chartData} 
            margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="month" 
            stroke="#6b7280"
            height={40}
            interval={4}
            fontSize={9}
            angle={-45}
            textAnchor="end"
            axisLine={{ stroke: '#6b7280' }}
            tickLine={false}
          />
          <YAxis 
            yAxisId="currency"
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={formatCurrency}
          />
          <YAxis 
            yAxisId="means"
            orientation="right"
            stroke="#6b7280"
            fontSize={10}
            domain={['dataMin', 'dataMax']}
            tickFormatter={() => ''}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Mean reference lines */}
          <ReferenceLine
            y={meanValues.totalIncome}
            stroke="#3b82f6"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            yAxisId="currency"
            label={{
              value: `μ ${formatCurrency(meanValues.totalIncome)}`,
              position: "right",
              offset: 5,
              style: { fontSize: '10px', fill: '#3b82f6', fontWeight: '600' }
            }}
          />
          <ReferenceLine
            y={meanValues.totalCOGS}
            stroke="#ef4444"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            yAxisId="currency"
            label={{
              value: `μ ${formatCurrency(meanValues.totalCOGS)}`,
              position: "right",
              offset: 5,
              style: { fontSize: '10px', fill: '#ef4444', fontWeight: '600' }
            }}
          />
          
          <Line 
            type="monotone" 
            dataKey="totalIncome" 
            stroke="#3b82f6" 
            strokeWidth={3}
            name="Total Income"
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
            yAxisId="currency"
          />
          <Line 
            type="monotone" 
            dataKey="totalCOGS" 
            stroke="#ef4444" 
            strokeWidth={3}
            name="Total COGS"
            dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2 }}
            yAxisId="currency"
          />
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};